import type { DashboardFilterConfig } from "./dashboardData";

/** One resolved active-filter chip: a field + value pair with display labels. */
export interface ActiveFilterChip {
  field: string;
  value: string;
  /** Dimension label, e.g. "Status". */
  fieldLabel: string;
  /** Value label, e.g. "Published" (falls back to the raw value). */
  valueLabel: string;
}

/**
 * Pure: flatten `selected` filter values into display chips, resolving labels
 * from the filter config. Unknown fields/values fall back to the raw string so
 * freeform (text/daterange) filters still render. Order follows `filters`, then
 * the selected-value order. Tested in dashboardActiveFilters.test.ts.
 */
export function activeFilterChips(
  filters: DashboardFilterConfig[],
  selected: Record<string, string[]>,
): ActiveFilterChip[] {
  const byField = new Map(filters.map((f) => [f.field, f]));
  const chips: ActiveFilterChip[] = [];
  // Known fields first, in config order…
  for (const config of filters) {
    for (const value of selected[config.field] ?? []) {
      chips.push(resolveChip(config.field, value, byField));
    }
  }
  // …then any selected fields not present in the config (defensive).
  for (const [field, values] of Object.entries(selected)) {
    if (byField.has(field)) continue;
    for (const value of values) chips.push(resolveChip(field, value, byField));
  }
  return chips;
}

function resolveChip(
  field: string,
  value: string,
  byField: Map<string, DashboardFilterConfig>,
): ActiveFilterChip {
  const config = byField.get(field);
  const valueLabel =
    config?.options?.find((o) => o.value === value)?.label ?? value;
  return { field, value, fieldLabel: config?.label ?? field, valueLabel };
}
